import { Component } from '@theme/component';
import { CartLinesUpdateEvent } from '@shopify/events';

/**
 * Trigger button that captures product context and opens the lens customization drawer.
 * @extends Component
 */
export class LensCustomizationTrigger extends Component {
  handleTriggerClick(event) {
    event?.stopPropagation();
    const productTitle = this.dataset.productTitle;
    const variantId = this.dataset.variantId;

    const component = document.querySelector('lens-customization-component');
    if (component) {
      component.startCustomization({ productTitle, variantId });
    }

    const drawer = document.getElementById('lens-drawer');
    if (drawer) {
      drawer.open();
    }
  }
}

/**
 * Dynamic multi-step lens customization wizard with cascading options,
 * automatic step skipping (missing brands/add-ons/measurements), and rich descriptions.
 *
 * Options JSON is either a legacy array of lens types, or an object:
 * {
 *   labels:     { design, index, coating },            // default step titles
 *   indexes:    { "1.60": { title, description } },    // shared row definitions for price grids
 *   coatings:   { "Plat": { title, description } },    // shared column definitions for price grids
 *   addons:     { "group_key": [ { title, price } ] }, // shared add-on lists, referenced by key
 *   lens_types: [ ... ]
 * }
 *
 * Any lens type, brand or option can hold nested single-choice `options` (with an
 * `options_label` step title), or a `matrix` price grid ({ cols, rows }) that expands
 * into an index step followed by a coating step whose prices are the full lens price.
 * The deepest selected node's `add-on` (array or group key) becomes the multi-select add-ons step.
 *
 * @extends Component
 */
export class LensCustomizationComponent extends Component {
  requiredRefs = ['stepsContainer', 'progressSteps', 'totalPrice', 'nextButton', 'nextButtonText'];

  /** @type {number} */
  #currentStepIndex = 0;

  /** @type {{ lensType: object|null, brand: object|null, path: object[], addons: object[] }} */
  #selections = { lensType: null, brand: null, path: [], addons: [] };

  /** @type {File|null} */
  #uploadedFile = null;

  /** @type {object|null} */
  #productContext = null;

  /** @type {object[]} Lens types (top level of the options tree) */
  #optionsData = [];

  /** @type {{ labels?: object, indexes?: object, coatings?: object, addons?: object }} Shared definitions */
  #definitions = {};

  /** @type {WeakMap<object, object[]>} Expanded price grids */
  #expandedMatrices = new WeakMap();

  /** @type {boolean} */
  #isSubmitting = false;

  /** @type {string} Key of the step on screen, so re-renders within a step don't animate */
  #renderedStepKey = '';

  /** @type {number|undefined} */
  #errorTimer;

  /** @type {number|undefined} Pending move to the next step after a single-choice pick */
  #advanceTimer;

  connectedCallback() {
    super.connectedCallback();
    this.#parseOptionsData();
    this.#setupDragAndDrop();
  }

  #parseOptionsData() {
    try {
      const el = document.getElementById('lens-options-data');
      if (el) {
        const parsed = JSON.parse(el.textContent);
        if (Array.isArray(parsed)) {
          this.#optionsData = parsed;
        } else if (parsed && Array.isArray(parsed.lens_types)) {
          this.#optionsData = parsed.lens_types;
          this.#definitions = parsed;
        }
      }
    } catch (e) {
      console.error('[lens-customization] Error parsing options data', e);
      this.#optionsData = [];
    }
  }

  #setupDragAndDrop() {
    const zone = this.refs.fileDropZone;
    if (!zone) return;

    zone.addEventListener('dragover', (e) => {
      e.preventDefault();
      zone.classList.add('lens-upload-zone--dragover');
    });

    zone.addEventListener('dragleave', (e) => {
      e.preventDefault();
      zone.classList.remove('lens-upload-zone--dragover');
    });

    zone.addEventListener('drop', (e) => {
      e.preventDefault();
      zone.classList.remove('lens-upload-zone--dragover');
      const file = e.dataTransfer?.files?.[0];
      if (file) this.#processFile(file);
    });
  }

  // ── Options Tree ───────────────────────────────────────────

  /**
   * Returns the single-choice child options of a node, expanding price grids.
   * @param {object|null} node
   * @returns {object[]}
   */
  #childOptions(node) {
    if (!node) return [];
    if (node.matrix) return this.#expandMatrix(node);
    return Array.isArray(node.options) ? node.options : [];
  }

  /**
   * Expands a `matrix` price grid into index options, each holding coating options.
   * @param {object} node
   * @returns {object[]}
   */
  #expandMatrix(node) {
    // Keyed by the matrix object so shallow copies of a selected node share the cache
    const cached = this.#expandedMatrices.get(node.matrix);
    if (cached) return cached;

    const { cols = [], rows = {} } = node.matrix;
    const { indexes = {}, coatings = {}, labels = {} } = this.#definitions;

    const expanded = Object.entries(rows)
      .map(([rowKey, prices]) => {
        const coatingOptions = cols
          .map((colKey, i) => {
            const price = Number(prices?.[i]) || 0;
            if (!price) return null;
            const def = coatings[colKey] || {};
            return { title: def.title || colKey, description: def.description || '', price };
          })
          .filter(Boolean);

        const def = indexes[rowKey] || {};
        return {
          title: def.title || rowKey,
          description: def.description || '',
          price: 0,
          options_label: labels.coating,
          options: coatingOptions,
        };
      })
      .filter((option) => option.options.length > 0);

    this.#expandedMatrices.set(node.matrix, expanded);
    return expanded;
  }

  /**
   * Step title for choosing among a node's child options.
   * @param {object} node
   * @returns {string}
   */
  #childStepLabel(node) {
    const labels = this.#definitions.labels || {};
    if (node.options_label) return node.options_label;
    if (node.matrix) return labels.index || 'Index';
    return labels.design || 'Options';
  }

  /**
   * Add-ons declared on a node, resolving shared group keys.
   * @param {object|null} node
   * @returns {object[]}
   */
  #nodeAddons(node) {
    if (!node) return [];
    const raw = node['add-on'] ?? node.addons;
    if (typeof raw === 'string') return this.#definitions.addons?.[raw] || [];
    return Array.isArray(raw) ? raw : [];
  }

  /** Node whose child options form the first option step (brand, or lens type without brands). */
  #optionsRoot() {
    const lensType = this.#selections.lensType;
    if (!lensType) return null;
    const brands = lensType.brands;
    if (Array.isArray(brands) && brands.length > 0) return this.#selections.brand;
    return lensType;
  }

  /**
   * Cheapest total reachable from a node, used for "from" prices.
   * @param {object} node
   * @returns {number}
   */
  #minTotal(node) {
    const own = Number(node.price) || 0;
    const children = Array.isArray(node.brands) && node.brands.length > 0 ? node.brands : this.#childOptions(node);
    if (children.length === 0) return own;
    return own + Math.min(...children.map((child) => this.#minTotal(child)));
  }

  // ── Dynamic Step Resolution ────────────────────────────────

  /**
   * Computes the list of active steps based on current selections.
   * Dynamically skips steps that have no options or where measurements is false.
   * @returns {{ id: 'lensType'|'brand'|'option'|'addons'|'upload', label: string, level?: number }[]}
   */
  #getActiveSteps() {
    const steps = [
      { id: 'lensType', label: this.#getStepLabel(0) || 'Lens Type' },
    ];

    const lensType = this.#selections.lensType;

    if (lensType) {
      const hasBrands = Array.isArray(lensType.brands) && lensType.brands.length > 0;
      if (hasBrands) {
        steps.push({ id: 'brand', label: this.#getStepLabel(1) || 'Brand' });
      }

      // Nested single-choice steps (design → index → coating …)
      let node = this.#optionsRoot();
      let level = 0;
      let pathComplete = !!node;
      while (node) {
        if (this.#childOptions(node).length === 0) break;
        steps.push({ id: 'option', level, label: this.#childStepLabel(node) });
        node = this.#selections.path[level] || null;
        if (!node) pathComplete = false;
        level++;
      }

      // Add-ons come from the deepest selected node that declares them
      if (pathComplete || !hasBrands) {
        if (this.#currentAddonOptions().length > 0) {
          steps.push({ id: 'addons', label: this.#getStepLabel(2) || 'Add-ons' });
        }
      }

      // Check if measurements upload is required/allowed
      if (lensType.measurements !== false) {
        steps.push({ id: 'upload', label: this.#getStepLabel(3) || 'Measurements' });
      }
    } else {
      // Before lens type is selected, show general flow preview
      steps.push({ id: 'brand', label: this.#getStepLabel(1) || 'Brand' });
      steps.push({ id: 'addons', label: this.#getStepLabel(2) || 'Add-ons' });
      steps.push({ id: 'upload', label: this.#getStepLabel(3) || 'Measurements' });
    }

    return steps;
  }

  /** Add-on options for the current selection path. */
  #currentAddonOptions() {
    const { lensType, brand, path } = this.#selections;
    const hasBrands = Array.isArray(lensType?.brands) && lensType.brands.length > 0;
    const chain = [hasBrands ? null : lensType, brand, ...path].filter(Boolean);

    for (let i = chain.length - 1; i >= 0; i--) {
      const addons = this.#nodeAddons(chain[i]);
      if (addons.length > 0) return addons;
    }
    return [];
  }

  // ── Wizard Start ───────────────────────────────────────────

  /**
   * Called by the trigger component to initialize the wizard.
   * @param {{ productTitle: string, variantId?: string }} context
   */
  startCustomization(context) {
    this.#productContext = context;
    this.#selections = { lensType: null, brand: null, path: [], addons: [] };
    this.#uploadedFile = null;
    this.#currentStepIndex = 0;
    this.#isSubmitting = false;
    this.#renderedStepKey = '';
    clearTimeout(this.#advanceTimer);

    if (this.refs.fileInput) this.refs.fileInput.value = '';
    this.querySelector('.lens-error')?.remove();

    this.#showFileZone(false);
    this.#showFilePreview(false);
    if (this.refs.stepsContainer) this.refs.stepsContainer.style.display = '';

    const footer = this.querySelector('.lens-footer');
    if (footer) footer.style.display = '';

    this.#renderCurrentStep();
    this.#updateProgress();
    this.#updatePrice();
    this.#updateNavButtons();
  }

  // ── Step Rendering ─────────────────────────────────────────

  #renderCurrentStep() {
    const container = this.refs.stepsContainer;
    if (!container) return;

    const activeSteps = this.#getActiveSteps();
    if (this.#currentStepIndex >= activeSteps.length) {
      this.#currentStepIndex = Math.max(0, activeSteps.length - 1);
    }

    const currentStep = activeSteps[this.#currentStepIndex];
    const stepKey = `${currentStep.id}-${currentStep.level ?? 0}`;
    const enterClass = stepKey === this.#renderedStepKey ? '' : ' lens-step--enter';
    this.#renderedStepKey = stepKey;
    container.style.display = '';

    // Upload Step: review of the selections above the upload zone
    if (currentStep.id === 'upload') {
      container.innerHTML = `
        <div class="lens-step${enterClass}">
          <p class="lens-step__title">${this.#escapeHtml(currentStep.label)}</p>
          ${this.#summaryHtml()}
        </div>`;
      container.scrollTop = 0;
      this.#showFileZone(!this.#uploadedFile);
      this.#showFilePreview(!!this.#uploadedFile);
      return;
    }

    this.#showFileZone(false);
    this.#showFilePreview(false);

    let options = [];
    let isMultiple = false;
    const stepTitle = currentStep.label;

    if (currentStep.id === 'lensType') {
      options = this.#optionsData;
    } else if (currentStep.id === 'brand') {
      options = this.#selections.lensType?.brands || [];
    } else if (currentStep.id === 'option') {
      const parent = currentStep.level === 0 ? this.#optionsRoot() : this.#selections.path[currentStep.level - 1];
      options = this.#childOptions(parent);
    } else if (currentStep.id === 'addons') {
      isMultiple = true;
      options = this.#currentAddonOptions();
    }

    if (options.length === 0) {
      container.innerHTML = `
        <div class="lens-step${enterClass}">
          <p class="lens-step__title">${this.#escapeHtml(stepTitle)}</p>
          <p style="color: rgb(var(--color-foreground-rgb) / 0.5); font-size: var(--font-size--2xs);">
            ${this.#escapeHtml(this.dataset.noOptionsText || 'No options available for this step.')}
          </p>
        </div>`;
      return;
    }

    const rowsHtml = options.map((opt, idx) => {
      const isSelected = this.#isOptionSelected(currentStep, idx);
      const selectedClass = isSelected ? ' lens-option--selected' : '';
      const desc = opt.descraption || opt.description || '';

      const indicator = isMultiple
        ? `<span class="lens-option-row__checkbox">
             ${isSelected ? '<svg width="14" height="14" viewBox="0 0 14 14" fill="none"><polyline points="11 4 5.5 10 3 7.5" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>' : ''}
           </span>`
        : `<span class="lens-option-row__radio">
             <span class="lens-option-row__radio-inner"></span>
           </span>`;

      const imageHtml = opt.image
        ? `<div class="lens-option-row__image-wrap">
             <img src="${this.#escapeHtml(opt.image)}" alt="${this.#escapeHtml(opt.title)}" loading="lazy">
           </div>`
        : '';

      return `
        <div class="lens-option-row${selectedClass}" data-index="${idx}" role="${isMultiple ? 'checkbox' : 'radio'}" tabindex="0" aria-checked="${isSelected}">
          <div class="lens-option-row__indicator">
            ${indicator}
          </div>
          ${imageHtml}
          <div class="lens-option-row__content">
            <div class="lens-option-row__header">
              <span class="lens-option-row__title">${this.#escapeHtml(opt.title)}</span>
              ${this.#optionPriceHtml(opt, isMultiple)}
            </div>
            ${desc ? `<p class="lens-option-row__description">${this.#escapeHtml(desc)}</p>` : ''}
          </div>
        </div>`;
    }).join('');

    const titleId = `lens-step-title-${stepKey}`;
    container.innerHTML = `
      <div class="lens-step${enterClass}">
        <p class="lens-step__title" id="${titleId}">${this.#escapeHtml(stepTitle)}</p>
        <div class="lens-options-list" role="${isMultiple ? 'group' : 'radiogroup'}" aria-labelledby="${titleId}">${rowsHtml}</div>
      </div>`;
    if (enterClass) container.scrollTop = 0;

    container.querySelectorAll('.lens-option-row').forEach((row) => {
      const handler = (e) => {
        if (e) {
          e.preventDefault();
          e.stopPropagation();
        }
        const idx = parseInt(row.dataset.index, 10);
        const hadFocus = row === document.activeElement;
        this.#handleOptionClick(currentStep, options[idx], idx);
        // Re-rendering replaces the rows, so keep keyboard focus on the same option
        if (hadFocus) container.querySelector(`.lens-option-row[data-index="${idx}"]`)?.focus();
      };
      row.addEventListener('click', handler);
      row.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          handler(e);
        }
      });
    });
  }

  /**
   * Price badge for an option row: "+X" for add-ons, "from X" when the
   * final price depends on later steps, otherwise the option's own price.
   * @param {object} opt
   * @param {boolean} isAddon
   * @returns {string}
   */
  #optionPriceHtml(opt, isAddon) {
    const price = Number(opt.price) || 0;

    if (isAddon) {
      return price > 0
        ? `<span class="lens-option-row__price">+${this.#formatMoney(price)}</span>`
        : `<span class="lens-option-row__price lens-option-row__price--free">${this.#escapeHtml(this.dataset.includedText || 'Included')}</span>`;
    }

    const hasChildren = (Array.isArray(opt.brands) && opt.brands.length > 0) || this.#childOptions(opt).length > 0;
    if (hasChildren) {
      const from = this.#minTotal(opt);
      return from > 0
        ? `<span class="lens-option-row__price lens-option-row__price--from">${this.#escapeHtml(this.dataset.fromText || 'From')} ${this.#formatMoney(from)}</span>`
        : '';
    }

    return price > 0
      ? `<span class="lens-option-row__price">${this.#formatMoney(price)}</span>`
      : `<span class="lens-option-row__price lens-option-row__price--free">${this.#escapeHtml(this.dataset.includedText || 'Included')}</span>`;
  }

  #isOptionSelected(step, idx) {
    if (step.id === 'lensType') return this.#selections.lensType?._idx === idx;
    if (step.id === 'brand') return this.#selections.brand?._idx === idx;
    if (step.id === 'option') return this.#selections.path[step.level]?._idx === idx;
    if (step.id === 'addons') return this.#selections.addons.some((a) => a._idx === idx);
    return false;
  }

  #handleOptionClick(step, optionData, idx) {
    const dataWithIdx = { ...optionData, _idx: idx };
    clearTimeout(this.#advanceTimer);

    // Re-picking the current choice keeps the later selections
    if (step.id !== 'addons' && this.#isOptionSelected(step, idx)) {
      this.#scheduleAdvance();
      return;
    }

    if (step.id === 'lensType') {
      this.#selections.lensType = dataWithIdx;
      this.#selections.brand = null;
      this.#selections.path = [];
      this.#selections.addons = [];
    } else if (step.id === 'brand') {
      this.#selections.brand = dataWithIdx;
      this.#selections.path = [];
      this.#selections.addons = [];
    } else if (step.id === 'option') {
      this.#selections.path = this.#selections.path.slice(0, step.level);
      this.#selections.path[step.level] = dataWithIdx;
      this.#selections.addons = [];
    } else if (step.id === 'addons') {
      const existingIdx = this.#selections.addons.findIndex((a) => a._idx === idx);
      if (existingIdx > -1) {
        this.#selections.addons.splice(existingIdx, 1);
      } else {
        this.#selections.addons.push(dataWithIdx);
      }
    }

    this.#renderCurrentStep();
    this.#updateProgress();
    this.#updatePrice();
    this.#updateNavButtons();

    if (step.id !== 'addons') this.#scheduleAdvance();
  }

  /**
   * Single-choice steps move on by themselves after a short pause, so the
   * shopper sees the selection land. Never submits: the last step needs the button.
   */
  #scheduleAdvance() {
    const fromIndex = this.#currentStepIndex;
    this.#advanceTimer = setTimeout(() => {
      const isLastStep = fromIndex >= this.#getActiveSteps().length - 1;
      if (this.#currentStepIndex !== fromIndex || isLastStep || this.#isSubmitting) return;
      this.handleNext();
    }, 300);
  }

  // ── Progress & Navigation ──────────────────────────────────

  #updateProgress() {
    const container = this.refs.progressSteps;
    if (!container) return;

    const activeSteps = this.#getActiveSteps();

    container.innerHTML = activeSteps.map((step, i) => {
      let state = 'upcoming';
      if (i < this.#currentStepIndex) state = 'completed';
      else if (i === this.#currentStepIndex) state = 'active';

      const connector = i < activeSteps.length - 1
        ? '<div class="lens-progress__connector"></div>'
        : '';

      return `
        <div class="lens-progress__step" data-state="${state}">
          <span class="lens-progress__dot"></span>
          <span class="lens-progress__label">${this.#escapeHtml(step.label)}</span>
        </div>
        ${connector}`;
    }).join('');
  }

  /** Sum of every selected item's price. */
  #calculateTotal() {
    const { lensType, brand, path, addons } = this.#selections;
    return [lensType, brand, ...path, ...addons]
      .filter(Boolean)
      .reduce((total, item) => total + (Number(item.price) || 0), 0);
  }

  #updatePrice() {
    if (this.refs.totalPrice) {
      this.refs.totalPrice.textContent = this.#formatMoney(this.#calculateTotal());
    }
  }

  #updateNavButtons() {
    const { prevButton, nextButton, nextButtonText } = this.refs;
    const activeSteps = this.#getActiveSteps();
    const isLastStep = this.#currentStepIndex === activeSteps.length - 1;
    const currentStep = activeSteps[this.#currentStepIndex];

    if (prevButton) {
      prevButton.style.display = this.#currentStepIndex === 0 ? 'none' : '';
    }

    if (nextButton && nextButtonText) {
      nextButtonText.textContent = isLastStep
        ? (this.dataset.submitText || 'Add to Cart')
        : (this.dataset.nextText || 'Next');

      const arrow = nextButton.querySelector('.lens-footer__btn-arrow');
      if (arrow) arrow.style.display = isLastStep ? 'none' : '';

      let disabled = false;
      if (currentStep) {
        if (currentStep.id === 'lensType' && !this.#selections.lensType) disabled = true;
        if (currentStep.id === 'brand' && !this.#selections.brand) disabled = true;
        if (currentStep.id === 'option' && !this.#selections.path[currentStep.level]) disabled = true;
        if (currentStep.id === 'upload' && this.dataset.uploadRequired === 'true' && !this.#uploadedFile) disabled = true;
      }

      nextButton.disabled = disabled || this.#isSubmitting;
    }
  }

  #getStepLabel(index) {
    try {
      const labels = JSON.parse(this.dataset.stepLabels || '[]');
      return labels[index] || '';
    } catch {
      return '';
    }
  }

  // ── Public Handlers (declarative events) ───────────────────

  handleNext(event) {
    event?.stopPropagation();
    clearTimeout(this.#advanceTimer);
    if (this.#isSubmitting) return;

    const activeSteps = this.#getActiveSteps();

    if (this.#currentStepIndex < activeSteps.length - 1) {
      this.#currentStepIndex++;
      this.#renderCurrentStep();
      this.#updateProgress();
      this.#updateNavButtons();
    } else {
      this.#submitToCart();
    }
  }

  handlePrev(event) {
    event?.stopPropagation();
    clearTimeout(this.#advanceTimer);
    if (this.#isSubmitting || this.#currentStepIndex === 0) return;

    this.#currentStepIndex--;
    this.#renderCurrentStep();
    this.#updateProgress();
    this.#updateNavButtons();
  }

  handleFileSelect(event) {
    event?.stopPropagation();
    const file = event.target?.files?.[0];
    if (file) this.#processFile(file);
  }

  handleRemoveFile(event) {
    event?.stopPropagation();
    this.#uploadedFile = null;
    if (this.refs.fileInput) this.refs.fileInput.value = '';
    this.#showFilePreview(false);
    this.#showFileZone(true);
    this.#updateNavButtons();
  }

  // ── File Processing ────────────────────────────────────────

  #processFile(file) {
    const maxSize = 10 * 1024 * 1024;
    const validTypes = ['image/jpeg', 'image/png', 'application/pdf'];

    if (!validTypes.includes(file.type)) {
      this.#showError(this.dataset.invalidTypeText || 'Invalid file type. Please upload JPG, PNG, or PDF.');
      return;
    }
    if (file.size > maxSize) {
      this.#showError(this.dataset.fileTooLargeText || 'File is too large. Maximum size is 10MB.');
      return;
    }

    this.#uploadedFile = file;

    if (this.refs.fileName) this.refs.fileName.textContent = file.name;

    if (this.refs.fileThumbnail) {
      if (file.type.startsWith('image/')) {
        const reader = new FileReader();
        reader.onload = (e) => {
          this.refs.fileThumbnail.innerHTML = `<img src="${e.target.result}" alt="${this.#escapeHtml(file.name)}">`;
        };
        reader.readAsDataURL(file);
      } else {
        this.refs.fileThumbnail.innerHTML = `
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" stroke-linecap="round" stroke-linejoin="round"/>
            <polyline points="14 2 14 8 20 8" stroke-linecap="round" stroke-linejoin="round"/>
            <line x1="16" y1="13" x2="8" y2="13" stroke-linecap="round"/>
            <line x1="16" y1="17" x2="8" y2="17" stroke-linecap="round"/>
          </svg>`;
      }
    }

    this.#showFileZone(false);
    this.#showFilePreview(true);
    this.#updateNavButtons();
  }

  #showFileZone(show) {
    if (this.refs.fileDropZone) {
      this.refs.fileDropZone.style.display = show ? '' : 'none';
    }
  }

  #showFilePreview(show) {
    if (this.refs.filePreview) {
      this.refs.filePreview.style.display = show ? '' : 'none';
    }
  }

  #showError(message) {
    const parent = this.querySelector('.lens-wizard') || this;
    let errorEl = parent.querySelector('.lens-error');
    if (!errorEl) {
      errorEl = document.createElement('div');
      errorEl.className = 'lens-error';
      errorEl.setAttribute('role', 'alert');
      const footer = parent.querySelector('.lens-footer');
      if (footer) {
        footer.insertBefore(errorEl, footer.firstChild);
      } else {
        parent.appendChild(errorEl);
      }
    }
    errorEl.innerHTML = `
      <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><circle cx="8" cy="8" r="7" stroke="currentColor" stroke-width="1.5"/><line x1="8" y1="5" x2="8" y2="9" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/><circle cx="8" cy="11.5" r="0.75" fill="currentColor"/></svg>
      <span>${this.#escapeHtml(message)}</span>`;

    clearTimeout(this.#errorTimer);
    this.#errorTimer = setTimeout(() => errorEl?.remove(), 8000);
  }

  // ── Cart Submission ────────────────────────────────────────

  /**
   * Builds the visible line item properties describing the selected lens.
   * @returns {Record<string, string>}
   */
  #buildLensProperties() {
    const properties = {};
    const label = (item) => {
      const price = Number(item.price) || 0;
      return price > 0 ? `${item.title} (${this.#formatMoney(price)})` : item.title;
    };

    if (this.#productContext?.productTitle) {
      properties['Frame'] = this.#productContext.productTitle;
    }

    for (const { key, items } of this.#selectedGroups()) {
      properties[key] = items.map(label).join(', ');
    }

    properties['Customization Total'] = this.#formatMoney(this.#calculateTotal());

    return properties;
  }

  /**
   * Selected items grouped by step, in wizard order.
   * `key` is the cart property name, `label` the step title shown to the shopper.
   * @returns {{ key: string, label: string, items: object[] }[]}
   */
  #selectedGroups() {
    const { lensType, brand, path, addons } = this.#selections;
    const groups = [];
    for (const step of this.#getActiveSteps()) {
      if (step.id === 'lensType' && lensType) groups.push({ key: 'Lens Type', label: step.label, items: [lensType] });
      if (step.id === 'brand' && brand) groups.push({ key: 'Brand', label: step.label, items: [brand] });
      // One property per nested step, keyed by that step's title (e.g. Design / Index / Coating)
      if (step.id === 'option' && path[step.level]) groups.push({ key: step.label, label: step.label, items: [path[step.level]] });
      if (step.id === 'addons' && addons.length > 0) groups.push({ key: 'Add-ons', label: step.label, items: addons });
    }
    return groups;
  }

  /** Review list of the current selections, shown on the measurements step. */
  #summaryHtml() {
    const rows = [];
    if (this.#productContext?.productTitle) {
      rows.push({ label: this.dataset.frameText || 'Frame', title: this.#productContext.productTitle, price: 0 });
    }
    for (const { label, items } of this.#selectedGroups()) {
      for (const item of items) rows.push({ label, title: item.title, price: Number(item.price) || 0 });
    }
    if (rows.length === 0) return '';

    return `
      <dl class="lens-summary">
        ${rows.map((row) => `
          <div class="lens-summary__row">
            <dt class="lens-summary__label">${this.#escapeHtml(row.label)}</dt>
            <dd class="lens-summary__value">
              <span class="lens-summary__title">${this.#escapeHtml(row.title)}</span>
              ${row.price ? `<span class="lens-summary__price">${this.#formatMoney(row.price)}</span>` : ''}
            </dd>
          </div>`).join('')}
      </dl>`;
  }

  /**
   * POSTs to the cart add endpoint and throws on Shopify errors.
   * @param {FormData|object} body
   * @returns {Promise<object>}
   */
  async #postCartAdd(body) {
    const cartAddUrl = window.Theme?.routes?.cart_add_url || '/cart/add.js';
    const isForm = body instanceof FormData;

    const response = await fetch(cartAddUrl, {
      method: 'POST',
      headers: isForm
        ? { Accept: 'application/json' }
        : { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: isForm ? body : JSON.stringify(body),
    });

    const data = await response.json();
    if (data.status || !response.ok) {
      throw new Error(data.description || data.message || 'Add to cart failed');
    }
    return data;
  }

  async #submitToCart() {
    const lensVariantId = this.dataset.lensVariantId;
    if (!lensVariantId) {
      this.#showError('No Custom Lens product configured. Please set it in Theme Settings.');
      return;
    }

    this.#isSubmitting = true;
    const { nextButton } = this.refs;
    if (nextButton) {
      nextButton.disabled = true;
      const spinner = nextButton.querySelector('.lens-footer__btn-spinner');
      const text = nextButton.querySelector('[ref="nextButtonText"]') || this.refs.nextButtonText;
      if (spinner) spinner.style.display = '';
      if (text) text.style.display = 'none';
    }

    const properties = this.#buildLensProperties();
    const total = this.#calculateTotal();

    // Section rendering IDs
    const sectionIds = [];
    document.querySelectorAll('cart-items-component').forEach((el) => {
      if (el instanceof HTMLElement && el.dataset.sectionId) {
        sectionIds.push(el.dataset.sectionId);
      }
    });

    const frameVariantId = this.#productContext?.variantId;

    // Quantity matches total in store currency for the 1.00 LE base product
    const lensQuantity = total > 0 ? Math.round(total) : 1;

    // The lens line is sent as multipart form data so the measurements file is
    // uploaded to Shopify and stored on the line item as a link.
    const lensForm = new FormData();
    lensForm.append('id', String(parseInt(lensVariantId, 10)));
    lensForm.append('quantity', String(lensQuantity));
    for (const [key, value] of Object.entries(properties)) {
      lensForm.append(`properties[${key}]`, value);
    }
    if (this.#uploadedFile) {
      lensForm.append(
        `properties[${this.dataset.measurementsProperty || 'Measurements'}]`,
        this.#uploadedFile,
        this.#uploadedFile.name
      );
    }
    if (!frameVariantId) lensForm.append('sections', sectionIds.join(','));

    const lines = [{ merchandiseId: String(lensVariantId), quantity: 1 }];
    if (frameVariantId) lines.unshift({ merchandiseId: String(frameVariantId), quantity: 1 });

    const deferredEventPromise = CartLinesUpdateEvent.createPromise();

    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines,
        promise: deferredEventPromise.promise,
      })
    );

    try {
      let data = await this.#postCartAdd(lensForm);

      // Add the eyeglasses frame product after the lens, so a failed upload never leaves a lone frame
      if (frameVariantId) {
        data = await this.#postCartAdd({
          items: [{
            id: parseInt(frameVariantId, 10),
            quantity: 1,
            properties: { '_lens_customization': 'Frame' },
          }],
          sections: sectionIds.join(','),
        });
      }

      const cartUrl = window.Theme?.routes?.cart_url || '/cart';
      const ajaxCart = await fetch(`${cartUrl}.json`, {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
      }).then((res) => res.json()).catch(() => null);

      if (ajaxCart) {
        deferredEventPromise.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(ajaxCart),
          detail: {
            items: ajaxCart.items,
            source: 'lens-customization-component',
            sourceId: this.id || 'lens-customization',
            itemCount: 1,
            sections: data.sections,
            didError: false,
          },
        });
      } else {
        deferredEventPromise.resolve({
          detail: {
            source: 'lens-customization-component',
            sourceId: this.id || 'lens-customization',
            itemCount: 1,
            sections: data.sections,
            didError: false,
          },
        });
      }

      this.#showSuccessState();

      setTimeout(() => {
        const lensDrawer = document.getElementById('lens-drawer');
        lensDrawer?.close();

        setTimeout(() => {
          const cartDrawer = document.getElementById('cart-drawer');
          if (cartDrawer && typeof cartDrawer.open === 'function') {
            cartDrawer.open();
          }
        }, 200);
      }, 1000);
    } catch (error) {
      deferredEventPromise.reject(error);
      console.error('[lens-customization] Cart error:', error);
      this.#showError(error.message || this.dataset.errorText || 'There was an error adding to cart. Please try again.');

      if (nextButton) {
        nextButton.disabled = false;
        const spinner = nextButton.querySelector('.lens-footer__btn-spinner');
        const text = nextButton.querySelector('[ref="nextButtonText"]') || this.refs.nextButtonText;
        if (spinner) spinner.style.display = 'none';
        if (text) text.style.display = '';
      }
    } finally {
      this.#isSubmitting = false;
    }
  }

  #showSuccessState() {
    const container = this.refs.stepsContainer;
    if (!container) return;

    container.style.display = '';
    this.#showFileZone(false);
    this.#showFilePreview(false);

    const successText = this.dataset.successText || 'Lens customization added to cart!';

    container.innerHTML = `
      <div class="lens-success">
        <div class="lens-success__icon">
          <svg width="28" height="28" viewBox="0 0 28 28" fill="none">
            <polyline points="20 8 11 18 7 14" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </div>
        <p class="lens-success__text">${this.#escapeHtml(successText)}</p>
      </div>`;

    const footer = this.closest('lens-customization-component')?.querySelector('.lens-footer');
    if (footer) footer.style.display = 'none';
  }

  // ── Helpers ────────────────────────────────────────────────

  /**
   * Formats a price in whole currency units using the shop money format,
   * with thousands separators like Shopify's `money` filter.
   * @param {number} amount
   * @returns {string}
   */
  #formatMoney(amount) {
    const num = Number(amount) || 0;
    const group = (value, decimals, thousands, decimal) => {
      const [int, frac] = value.toFixed(decimals).split('.');
      const grouped = int.replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
      return frac ? `${grouped}${decimal}${frac}` : grouped;
    };
    const format = this.dataset.moneyFormat || '{{amount}} EGP';

    return format.replace(/\{\{\s*(\w+)\s*\}\}/g, (match, placeholder) => {
      switch (placeholder) {
        case 'amount': return group(num, 2, ',', '.');
        case 'amount_no_decimals': return group(num, 0, ',', '.');
        case 'amount_with_comma_separator': return group(num, 2, '.', ',');
        case 'amount_no_decimals_with_comma_separator': return group(num, 0, '.', ',');
        case 'amount_with_apostrophe_separator': return group(num, 2, "'", '.');
        case 'amount_no_decimals_with_space_separator': return group(num, 0, ' ', '.');
        case 'amount_with_space_separator': return group(num, 2, ' ', ',');
        case 'amount_with_period_and_space_separator': return group(num, 2, ' ', '.');
        default: return match;
      }
    });
  }

  #escapeHtml(str) {
    const div = document.createElement('div');
    div.textContent = str || '';
    return div.innerHTML;
  }
}

if (!customElements.get('lens-customization-trigger')) {
  customElements.define('lens-customization-trigger', LensCustomizationTrigger);
}

if (!customElements.get('lens-customization-component')) {
  customElements.define('lens-customization-component', LensCustomizationComponent);
}
